import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Copy, Check, Share2, Sparkles, ExternalLink, Wallet, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const statusPill = (s) => {
  const map = {
    holding: { label: "Holding · 30d", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    available: { label: "Available", cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
    paid: { label: "Paid", cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
    clawed_back: { label: "Clawed back", cls: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  };
  return map[s] || { label: s, cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/30" };
};

const Referrals = () => {
  const [me, setMe] = useState(null);
  const [credits, setCredits] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meR, credR, payR] = await Promise.all([
        api.get("/referrals/me"),
        api.get("/referrals/credits"),
        api.get("/referrals/payouts"),
      ]);
      setMe(meR.data);
      setCredits(credR.data || []);
      setPayouts(payR.data || []);
    } catch (e) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const shareUrl = useMemo(() => {
    if (!me?.code) return "";
    // Use the current origin so preview + prod both work; deploy tenant can
    // still hand out quillpayed.com/?ref= manually in marketing copy.
    const origin = typeof window !== "undefined" ? window.location.origin : "https://quillpayed.com";
    return `${origin}/?ref=${me.code}`;
  }, [me]);

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Get paid in crypto with Quill",
          text: "I use Quill to invoice clients + creators in ETH/BTC. First month on me:",
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  if (loading || !me) {
    return (
      <div data-testid="referrals-page-loading" className="p-10 text-zinc-500 text-sm">
        Loading…
      </div>
    );
  }

  const pct = Math.round((me.commission_pct || 0.2) * 100);

  return (
    <div data-testid="referrals-page">
      <PageHeader
        overline="Growth"
        title="Refer & earn"
        description={`Earn ${pct}% of every paid subscription — for 12 months. Paid in crypto to your wallet.`}
        actions={
          <Link
            to="/leaderboard"
            data-testid="referrals-view-leaderboard-btn"
            target="_blank"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(180deg, rgba(232, 185, 108, 0.14), rgba(232, 185, 108, 0.06))",
              border: "1px solid rgba(232, 185, 108, 0.3)",
              color: "#E8B96C",
            }}
          >
            <Trophy size={14} /> Public leaderboard
            <ExternalLink size={12} />
          </Link>
        }
      />

      {/* Hero card — share link */}
      <div className="px-4 md:px-8 pt-6">
        <div
          className="rounded-2xl p-6 md:p-8"
          style={{
            background:
              "linear-gradient(135deg, hsl(var(--quill-info) / 0.85) 0%, hsl(var(--quill-warm) / 0.85) 60%, hsl(var(--quill-success) / 0.85) 100%)",
            color: "hsl(var(--quill-ink-warm))",
          }}
          data-testid="referral-hero"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] opacity-80">
            <Sparkles size={12} /> Your share link
          </div>
          <div className="mt-2 font-mono text-xl md:text-2xl break-all" data-testid="referral-share-url">
            {shareUrl}
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              onClick={copyLink}
              data-testid="referral-copy-btn"
              className="inline-flex items-center justify-center gap-2 bg-black/85 text-white text-sm font-medium px-4 py-2.5 rounded-md hover:bg-black"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy link"}
            </button>
            <button
              onClick={nativeShare}
              data-testid="referral-share-btn"
              className="inline-flex items-center justify-center gap-2 border border-black/40 text-black/85 text-sm font-medium px-4 py-2.5 rounded-md hover:bg-black/5"
            >
              <Share2 size={14} /> Share
            </button>
            <div className="sm:ml-auto text-xs opacity-80 self-center max-w-xs">
              Attribution lasts 30 days. Minimum payout ${me.payout_min_usd?.toFixed?.(0) ?? "25"}.
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#2c241e] mt-6">
        <StatCard label="Conversions" value={String(me.stats.conversions)} sub="Paid subs from you" testId="ref-stat-conversions" />
        <StatCard label="Available" value={fmtMoney(me.stats.available_usd)} sub="Ready to withdraw" accent="success" testId="ref-stat-available" />
        <StatCard label="Holding" value={fmtMoney(me.stats.holding_usd)} sub="30-day clearing" accent="warning" testId="ref-stat-holding" />
        <StatCard label="Paid out" value={fmtMoney(me.stats.paid_usd)} sub="Lifetime" testId="ref-stat-paid" />
      </div>

      {/* Ledger */}
      <div className="p-4 md:p-8 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Recent commissions</div>
          {credits.length === 0 ? (
            <div className="border border-[#2c241e] p-10 text-center" data-testid="referrals-empty">
              <div className="text-sm text-zinc-400 max-w-md mx-auto">
                No commissions yet. When someone signs up with your link and starts a paid plan,{" "}
                you&apos;ll earn <span className="text-white">{pct}%</span> of every one of their sub cycles — for 12 months.
              </div>
              <a
                href="/pricing"
                className="inline-flex items-center gap-1 mt-4 text-amber-300 hover:opacity-80 text-xs"
                data-testid="referrals-pricing-link"
              >
                See plan pricing <ExternalLink size={11} />
              </a>
            </div>
          ) : (
            <div className="border border-[#2c241e]" data-testid="referrals-credits-table">
              <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <div className="col-span-3">Earned</div>
                <div className="col-span-4">Referred</div>
                <div className="col-span-2 text-right">You earn</div>
                <div className="col-span-3">Status</div>
              </div>
              {credits.map((c) => {
                const pill = statusPill(c.status);
                return (
                  <div
                    key={c.credit_id}
                    data-testid={`referral-credit-row-${c.credit_id}`}
                    className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] last:border-b-0 items-center text-sm"
                  >
                    <div className="col-span-3 text-zinc-400 text-xs">{fmtDate(c.earned_at)}</div>
                    <div className="col-span-4 min-w-0">
                      <div className="truncate text-white">{c.referred_email_masked}</div>
                      <div className="text-[11px] text-zinc-500">{c.tier} · {c.interval}</div>
                    </div>
                    <div className="col-span-2 text-right font-mono">
                      {fmtMoney(c.amount_usd)}
                      <div className="text-[10px] text-zinc-500">of {fmtMoney(c.subscription_usd)}</div>
                    </div>
                    <div className="col-span-3">
                      <span className={`inline-flex items-center gap-1 border text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded ${pill.cls}`}>
                        {pill.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payouts + how it works */}
        <div className="md:col-span-4 space-y-6">
          <div className="border border-[#2c241e] p-5" data-testid="referrals-payouts">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3 flex items-center gap-2">
              <Wallet size={12} /> Payouts
            </div>
            {payouts.length === 0 ? (
              <div className="text-xs text-zinc-500">
                No payouts yet. Earnings clear after 30 days and are sent to your Quill wallet address once you cross ${me.payout_min_usd?.toFixed?.(0) ?? "25"}.
              </div>
            ) : (
              <ul className="space-y-3">
                {payouts.slice(0, 8).map((p) => (
                  <li key={p.payout_id} className="text-xs flex items-center justify-between gap-2" data-testid={`payout-row-${p.payout_id}`}>
                    <div>
                      <div className="text-zinc-400">{fmtDate(p.created_at)}</div>
                      {p.tx_hash && (
                        <div className="text-[10px] font-mono text-zinc-600 truncate max-w-[10rem]">{p.tx_hash}</div>
                      )}
                    </div>
                    <div className="font-mono text-white">{fmtMoney(p.amount_usd)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border border-[#2c241e] p-5 text-xs text-zinc-400 leading-relaxed">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">How it works</div>
            <ol className="list-decimal list-inside space-y-1.5">
              <li>Share your link. Every visitor is attributed to you for 30 days.</li>
              <li>They sign up and pay for Pro, Team, or Studio.</li>
              <li>You earn <span className="text-white">{pct}%</span> of every one of their sub cycles for 12 months.</li>
              <li>After a 30-day holding window, earnings become available.</li>
              <li>Above ${me.payout_min_usd?.toFixed?.(0) ?? "25"}, we send USDC/ETH straight to your wallet.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
